import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Award, MapPin, Calendar, Users, Star, CheckCircle, Truck, AlertTriangle, Check, X, MessageSquare } from 'lucide-react';
import { getVendorDisplayName } from '@/utils/vendorHelpers';
import { Review } from '@/lib/types/review';
import { ReviewCard } from '@/components/reviews/ReviewCard';

interface GalleryImage {
  url: string;
  uploaded_at: string;
  storage_key: string;
}

interface VendorDetailSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  certifications: any[];
  capabilities: any[];
  reviewAggregate: any;
  reviews?: Review[];
  loading?: boolean;
  isSelected?: boolean;
  onSelectVendor?: () => void;
}

export function VendorDetailSlideOver({ 
  isOpen, 
  onClose, 
  profile, 
  certifications, 
  capabilities, 
  reviewAggregate,
  reviews,
  loading,
  isSelected,
  onSelectVendor
}: VendorDetailSlideOverProps) {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const displayedReviews = showAllReviews ? reviews : reviews?.slice(0, 3);
  const getVerificationBadge = (profile: any) => {
    if (!profile) return null;
    
    if (profile.is_verified) {
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Verified
        </Badge>
      );
    }
    
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        Unverified
      </Badge>
    );
  };

  const formatScore = (score: number | null) => {
    if (!score) return 'N/A';
    return score.toFixed(1);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="pb-4 border-b flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Vendor Details
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="space-y-4 py-6 flex-1 overflow-y-auto">
            <div className="animate-pulse space-y-3">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
              <div className="grid grid-cols-2 gap-3 mt-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-24 bg-muted rounded" />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-6 py-6 flex-1 overflow-y-auto">
              {/* Company Header */}
              <div className="text-center space-y-3">
                <h2 className="text-2xl font-bold">{getVendorDisplayName(profile)}</h2>
                {profile?.business_description && (
                  <p className="text-sm text-muted-foreground">
                    {profile.business_description}
                  </p>
                )}
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {getVerificationBadge(profile)}
                  <Badge variant="outline">{profile?.verification_level || 'Basic'}</Badge>
                </div>
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <Users className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">
                      {profile?.employee_count_range || 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Employees</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3 text-center">
                    <Calendar className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">
                      {profile?.founded_year || 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Founded</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3 text-center">
                    <MapPin className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">
                      {profile?.state || 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Location</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3 text-center">
                    <CheckCircle className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">
                      {profile?.total_jobs_completed || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">Jobs Completed</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3 text-center">
                    <Star className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">
                      {formatScore(reviewAggregate?.avg_overall_rating || profile?.average_rating)}
                    </div>
                    <div className="text-xs text-muted-foreground">Overall Rating</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3 text-center">
                    <Truck className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">
                      {profile?.on_time_delivery_rate != null ? `${profile.on_time_delivery_rate}%` : 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">On-Time Delivery</div>
                  </CardContent>
                </Card>
              </div>

              {/* Review Scores */}
              {reviewAggregate && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                      <Star className="h-4 w-4" />
                      Average Review Scores
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <div className="text-xl font-bold text-primary">
                          {formatScore(reviewAggregate.avg_quality_rating)}
                        </div>
                        <div className="text-xs text-muted-foreground">Quality</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-primary">
                          {formatScore(reviewAggregate.avg_timeliness_rating)}
                        </div>
                        <div className="text-xs text-muted-foreground">Timeliness</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-primary">
                          {formatScore(reviewAggregate.avg_communication_rating)}
                        </div>
                        <div className="text-xs text-muted-foreground">Communication</div>
                      </div>
                    </div>
                  </CardContent>
              </Card>
              )}

              {/* Certifications */}
              {certifications && certifications.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                      <Award className="h-4 w-4" />
                      Certifications ({certifications.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {certifications.map((cert, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {cert.certification_type?.name || cert.certification_types?.name || cert.certification_name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Capabilities */}
              {capabilities && capabilities.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      Capabilities ({capabilities.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {capabilities.map((capability, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {capability.capability?.name || 'Unknown'}
                          {capability.business_percentage > 0 && ` (${capability.business_percentage}%)`}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Disputes */}
              {profile?.dispute_count !== undefined && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                      <AlertTriangle className={`h-4 w-4 ${(profile.dispute_count || 0) === 0 ? 'text-primary' : 'text-yellow-500'}`} />
                      Dispute History
                    </h3>
                    <div className={`text-lg font-bold ${(profile.dispute_count || 0) === 0 ? 'text-primary' : 'text-yellow-500'}`}>
                      {profile.dispute_count || 0} disputes
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Gallery */}
              {profile?.gallery_images && profile.gallery_images.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3 text-sm">Gallery</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {(profile.gallery_images as GalleryImage[]).slice(0, 4).map((image, index) => (
                        <div key={image.storage_key || index} className="aspect-square rounded-lg overflow-hidden border bg-muted">
                          <img
                            src={image.url}
                            alt={`Gallery ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                    {profile.gallery_images.length > 4 && (
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        +{profile.gallery_images.length - 4} more images
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Recent Reviews */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                    <MessageSquare className="h-4 w-4" />
                    Recent Reviews
                    {reviews && reviews.length > 0 && (
                      <span className="text-sm font-normal text-muted-foreground">
                        ({reviews.length})
                      </span>
                    )}
                  </h3>
                  
                  {!reviews || reviews.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">
                      No reviews yet
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {displayedReviews?.map((review) => (
                        <ReviewCard 
                          key={review.id} 
                          review={review}
                          reviewerName="Client"
                        />
                      ))}
                      
                      {reviews.length > 3 && !showAllReviews && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowAllReviews(true)}
                          className="w-full"
                        >
                          Show all {reviews.length} reviews
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sticky Footer with Select/Deselect Button */}
            {onSelectVendor && (
              <div className="flex-shrink-0 border-t pt-4 pb-2 bg-background">
                {isSelected ? (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      onSelectVendor();
                      onClose();
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove from RFQ
                  </Button>
                ) : (
                  <Button 
                    className="w-full"
                    onClick={() => {
                      onSelectVendor();
                      onClose();
                    }}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Select for RFQ
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
